import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFleetStats } from '@/hooks';
import { useRecentWorkspaces } from '@/hooks/useWorkspaces';
import { useFailures } from '@/hooks/useFailures';
import { useForecastProject } from '@/hooks/useForecastProject';
import { AllWorkspacesModal, LoadWorkspaceModal } from '@/components/workspaces';
import { AllForecastsModal, LoadForecastDialog } from '@/components/forecast';
import {
  StatsCard,
  ContentCard,
} from '@/components/ui';
import { DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import {
  UserIcon,
  CheckBadgeIcon,
  ArrowDownCircleIcon,
  ClockIcon,
  PlusIcon,
  FolderOpenIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { useLoadWorkspaceToAnalysis } from '@/hooks/useLoadWorkspaceToAnalysis';
import { LoadWorkspaceOptions } from '@/types/workspace';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const loadWorkspaceMutation = useLoadWorkspaceToAnalysis("weibull");

  // Modal states
  const [showLoadWorkspaceModal, setShowLoadWorkspaceModal] = useState(false);
  const [showLoadForecastDialog, setShowLoadForecastDialog] = useState(false);

  // All workspaces modal state
  const [showAllWorkspacesModal, setShowAllWorkspacesModal] = useState(false);

  // All forecasts modal state
  const [showAllForecastsModal, setShowAllForecastsModal] = useState(false);

  // Get fleet statistics using reusable hooks
  const { fleetTurbineCount, totalSiteCount, isLoading: fleetLoading } = useFleetStats();

  // Get recent workspaces for analyses display
  const {
    data: recentWorkspaces,
    isLoading: workspacesLoading,
    error: workspacesError,
  } = useRecentWorkspaces(3);

  // Get total failures count efficiently
  const { data: failuresData, isLoading: failuresLoading } = useFailures({
    ...DEFAULT_FAILURE_FILTERS,
    size: 1, // We only need the total_count, not the actual data
    page: 1,
  });

  // Get recent forecasts
  const { forecastProjects, isLoadingList: forecastsLoading } = useForecastProject();

  // Transform workspace data into recent analyses format
  const recentAnalyses =
    recentWorkspaces?.workspaces
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .map((workspace) => ({
        id: workspace.id,
        name: workspace.name,
        sampleSize: workspace.metadata.totalFailures || 0,
        updatedAt: new Date(workspace.updatedAt).toLocaleDateString(),
        createdAt: new Date(workspace.createdAt).toLocaleDateString(),
        component:
          workspace.metadata.componentsAnalyzed.length > 0
            ? workspace.metadata.componentsAnalyzed.length > 2
              ? `${workspace.metadata.componentsAnalyzed.slice(0, 2).join(', ')} +${workspace.metadata.componentsAnalyzed.length - 2}`
              : workspace.metadata.componentsAnalyzed.join(', ')
            : 'Multiple',
        version: workspace.version,
        createdBy: workspace.createdBy, // Include creator info
        isLatestData: workspace.isLatestData, // Include data freshness
      })) || [];

  // Calculate active analyses count from workspaces
  const activeAnalysesCount = recentWorkspaces?.total || 0;

  // Transform forecast data into recent forecasts format (limit to 3 most recent)
  const recentForecasts = forecastProjects
    .sort((a, b) => new Date(b.metadata.updatedAt).getTime() - new Date(a.metadata.updatedAt).getTime())
    .slice(0, 3)
    .map((forecast) => ({
      id: forecast.id,
      name: forecast.name,
      sourceAnalysisName: forecast.sourceAnalysisName || 'Unknown Analysis',
      horizonYears: forecast.configuration?.forecastParams?.horizonYears || 'N/A',
      updatedAt: new Date(forecast.metadata.updatedAt).toLocaleDateString(),
      createdAt: new Date(forecast.metadata.createdAt).toLocaleDateString(),
      resultsCount: forecast.results?.length || 0,
      createdBy: {
        name: forecast.metadata.userId || 'Unknown User'
      }
    }));

  // Dashboard stats with real fleet data and workspace data
  const dashboardStats = [
    {
      title: 'Total Failures',
      value: failuresData?.total_count?.toString() || '0',
      color: 'bright-blue',
      description: 'Fleet-wide failure records',
      loading: failuresLoading,
    },
    {
      title: 'Saved Analyses',
      value: activeAnalysesCount.toString(),
      color: 'purple',
      description: 'Completed Weibull studies',
      loading: workspacesLoading,
    },
    {
      title: 'Saved Forecasts',
      value: forecastProjects.length.toString(),
      color: 'green',
      description: 'Future failure predictions',
      loading: forecastsLoading,
    },
    {
      title: 'Fleet Turbines',
      value: fleetTurbineCount,
      color: 'fleet-monitor-blue',
      description: 'Total across all sites',
      loading: fleetLoading,
    },
    {
      title: 'Sites Monitored',
      value: totalSiteCount,
      color: 'dark-blue',
      description: 'Wind farm locations',
      loading: fleetLoading,
    },
  ];



  const handleLoadAnalysis = () => {
    setShowLoadWorkspaceModal(true);
  };

  const handleLoadForecast = () => {
    setShowLoadForecastDialog(true);
  };

  // Analysis click handler - navigate directly to Analysis page
  const handleAnalysisClick = async (workspace: { id: string; name: string; version: number }) => {
    // Load workspace 
    const options: LoadWorkspaceOptions = {
      strategy: 'exact_recreation',
      preserveData: true,
      preserveResults: true,
      preserveFilters: true,
    };

    const response = await loadWorkspaceMutation.mutateAsync({
      id: workspace.id,
      options,
    });
    // Navigate to Analysis page
    navigate('/analysis', { state: { workspace: response.workspace } });
  };

  // Load workspace handler - for LoadWorkspaceModal
  const handleLoadWorkspace = (workspace: { id: string; name: string; version?: number }) => {
    navigate('/analysis', { state: { workspace: workspace } });
  };

  // Load forecast handler - for LoadForecastDialog
  const handleLoadForecastFromDialog = (forecast: { id: string; name: string }) => {
    setShowLoadForecastDialog(false);
    navigate('/forecast', { state: { forecastId: forecast.id } });
  };

  // All workspaces modal handlers
  const handleWorkspaceClick = (workspace: { id: string; name: string; version: number }) => {
    // Close all workspaces modal
    setShowAllWorkspacesModal(false);

    // Navigate to Analysis page with workspace
    handleAnalysisClick(workspace);
  };

  // All forecasts modal handlers
  const handleForecastClick = (forecast: { id: string; name: string }) => {
    // Close all forecasts modal
    setShowAllForecastsModal(false);

    // Navigate to forecast page with the specific forecast ID
    navigate('/forecast', { state: { forecastId: forecast.id } });
  };

  // Removed version history handlers as we now load directly
  // These can be restored when version history backend is implemented

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dashboard Header - matching forecasting page format */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Title | Interactions with other pages/analysis */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-5 w-5 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">Analysis Dashboard</h1>
            </div>

            <div className="h-6 w-px bg-gray-300" />

            <button
              onClick={() => navigate('/analysis')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Analysis
            </button>

            <button
              onClick={() => navigate('/forecast')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              New Forecast
            </button>
          </div>

          {/* Right side - Key Info | Interactions with this page */}
          <div className="flex items-center space-x-4">

            <div className="h-6 w-px bg-gray-300" />

            <button
              onClick={handleLoadAnalysis}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <FolderOpenIcon className="h-4 w-4 mr-2" />
              Load Workspace
            </button>

            <button
              onClick={handleLoadForecast}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <FolderOpenIcon className="h-4 w-4 mr-2" />
              Load Forecast
            </button>
          </div>
        </div>
      </div>


      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {dashboardStats.map((stat, index) => (
            <StatsCard
              key={index}
              title={stat.title}
              value={stat.value}
              description={stat.description}
              color={
                stat.color as
                | 'bright-blue'
                | 'purple'
                | 'fleet-monitor-blue'
                | 'dark-blue'
                | 'green'
                | 'sky-blue'
              }
              loading={stat.loading}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Analyses Section */}
          <ContentCard
            title="Recent Analyses"
            subtitle="Latest saved Weibull analysis workspaces"
            headerBorder={true}
            loading={workspacesLoading}
            actions={
              <button
                onClick={() => setShowAllWorkspacesModal(true)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                View All
              </button>
            }
          >
            {workspacesError ? (
              <div className="p-4 text-center text-gray-500">
                <div className="text-sm text-red-600 mb-2">Failed to load recent analyses</div>
                <div className="text-xs text-gray-400">Please try refreshing the page</div>
              </div>
            ) : recentAnalyses.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <div className="text-sm mb-2">No saved analyses yet</div>
                <div className="text-xs text-gray-400">
                  Create your first Weibull analysis to see it here
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {recentAnalyses.map((analysis) => (
                  <div
                    key={analysis.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() =>
                      handleAnalysisClick({
                        id: analysis.id,
                        name: analysis.name,
                        version: analysis.version,
                      })
                    }
                    title="Click to load analysis"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <div className="onyx-subtitle text-gray-900">{analysis.name}</div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          v{analysis.version}
                        </span>
                        {/* Data freshness indicator using icon */}
                        <div className="group relative">
                          {analysis.isLatestData ? (
                            <CheckBadgeIcon className="w-4 h-4 text-green-600" />
                          ) : (
                            <ArrowDownCircleIcon className="w-4 h-4 text-amber-600" />
                          )}
                          <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                            {analysis.isLatestData
                              ? 'Uses latest dataset'
                              : 'Uses historical data'}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        {analysis.component} • {analysis.sampleSize} samples
                      </div>
                      {analysis.createdBy && (
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <UserIcon className="w-3 h-3" />
                          <span>by {analysis.createdBy.name}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-900 font-medium">
                        Updated {analysis.updatedAt}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Created {analysis.createdAt}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ContentCard>

          {/* Recent Forecasts Section */}
          <ContentCard
            title="Recent Forecasts"
            subtitle="Latest failure prediction analyses"
            headerBorder={true}
            loading={forecastsLoading}
            actions={
              <button
                onClick={() => setShowAllForecastsModal(true)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                View All
              </button>
            }
          >
            {recentForecasts.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <div className="text-sm mb-2">No saved forecasts yet</div>
                <div className="text-xs text-gray-400">
                  Create your first forecast from a Weibull analysis to see it here
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {recentForecasts.map((forecast) => (
                  <div
                    key={forecast.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => navigate('/forecast', { state: { forecastId: forecast.id } })}
                    title="Click to view forecast"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <div className="onyx-subtitle text-gray-900">{forecast.name}</div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {forecast.horizonYears}y horizon
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        Source: {forecast.sourceAnalysisName} • {forecast.resultsCount} periods
                      </div>
                      {forecast.createdBy && (
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <UserIcon className="w-3 h-3" />
                          <span>by {forecast.createdBy.name}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-900 font-medium">
                        Updated {forecast.updatedAt}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Created {forecast.createdAt}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ContentCard>

        </div>
      </div>

      {/* All Workspaces Modal */}
      <AllWorkspacesModal
        isOpen={showAllWorkspacesModal}
        onClose={() => setShowAllWorkspacesModal(false)}
        onWorkspaceClick={handleWorkspaceClick}
      />

      {/* All Forecasts Modal */}
      <AllForecastsModal
        isOpen={showAllForecastsModal}
        onClose={() => setShowAllForecastsModal(false)}
        onForecastClick={handleForecastClick}
      />

      {/* Load Workspace Modal */}
      <LoadWorkspaceModal
        isOpen={showLoadWorkspaceModal}
        onClose={() => setShowLoadWorkspaceModal(false)}
        onLoadWorkspace={handleLoadWorkspace}
        workspaceType="weibull"
      />

      {/* Load Forecast Dialog */}
      <LoadForecastDialog
        open={showLoadForecastDialog}
        onClose={() => setShowLoadForecastDialog(false)}
        onSelect={handleLoadForecastFromDialog}
      />

      {/* Version History Modal - Removed until backend support is available */}
    </div>
  );
};

export default Dashboard;
